/**
 * Клиентская проверка креативов до отправки на сервер.
 *
 * Смысл в скорости обратной связи: узнать про 12-мегабайтный файл
 * сразу, а не после сорока секунд загрузки и ответа 413.
 */

/** Разбирает "3:4" в число 0.75. Возвращает null, если формат не задан. */
const parseAspect = (value) => {
    if (!value || typeof value !== 'string') return null;
    const [w, h] = value.split(':').map(Number);
    if (!w || !h) return null;
    return w / h;
};

export const readImageMeta = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Не удалось прочитать изображение'));
    };
    img.src = url;
});

export const readVideoMeta = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({
            duration: Math.round(video.duration || 0),
            width: video.videoWidth,
            height: video.videoHeight,
        });
    };
    video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Не удалось прочитать видео'));
    };
    video.src = url;
});


/**
 * Проверяет изображение по media_spec площадки.
 * Возвращает { ok, errors: [], warnings: [], meta }.
 *
 * Несовпадение пропорций — предупреждение, а не ошибка: изображение
 * можно обрезать по центру, и запрещать это было бы излишне строго.
 * Недостаточное разрешение и превышение веса — именно ошибки.
 */
export async function validateImage(file, spec = {}) {
    const errors = [];
    const warnings = [];

    if (!file.type.startsWith('image/')) {
        return { ok: false, errors: ['Нужен файл изображения'], warnings: [], meta: null };
    }

    const maxMb = Number(spec.max_size_mb || 5);
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > maxMb) {
        errors.push(`Файл весит ${sizeMb.toFixed(1)} МБ, максимум ${maxMb} МБ`);
    }

    let meta = null;
    try {
        meta = await readImageMeta(file);
    } catch (e) {
        return { ok: false, errors: ['Файл повреждён или это не изображение'], warnings: [], meta: null };
    }

    const minWidth = Number(spec.min_width || 0);
    if (minWidth && meta.width < minWidth) {
        errors.push(
            `Ширина ${meta.width} px, нужно минимум ${minWidth} px — иначе картинка будет размытой`
        );
    }

    const targetRatio = parseAspect(spec.aspect);
    if (targetRatio) {
        const actual = meta.width / meta.height;
        const deviation = Math.abs(actual - targetRatio) / targetRatio;
        if (deviation > 0.08) {
            warnings.push(
                `Пропорции ${meta.width}×${meta.height} отличаются от рекомендованных ${spec.aspect} — ` +
                `изображение будет обрезано по центру`
            );
        }
    }

    return { ok: errors.length === 0, errors, warnings, meta };
}


export async function validateVideo(file, spec = {}) {
    const errors = [];
    const warnings = [];

    if (!file.type.startsWith('video/')) {
        return { ok: false, errors: ['Нужен видеофайл'], warnings: [], meta: null };
    }

    const maxMb = Number(spec.max_size_mb || 10);
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > maxMb) {
        errors.push(`Файл весит ${sizeMb.toFixed(1)} МБ, максимум ${maxMb} МБ`);
    }

    let meta = null;
    try {
        meta = await readVideoMeta(file);
    } catch (e) {
        return { ok: false, errors: ['Не удалось прочитать видео'], warnings: [], meta: null };
    }

    const maxDuration = Number(spec.max_duration_sec || 30);
    if (meta.duration > maxDuration) {
        errors.push(`Длительность ${meta.duration} сек, максимум ${maxDuration} сек`);
    }
    if (meta.duration < 3) {
        warnings.push('Видео короче 3 секунд — пользователь не успеет ничего понять');
    }

    return { ok: errors.length === 0, errors, warnings, meta };
}


/**
 * Самое строгое ограничение по текстам среди площадок,
 * поддерживающих формат объявления.
 *
 * Если объявление крутится на трёх площадках, а заголовок влезает
 * только в две — в третьей он обрежется, и блок будет выглядеть сломанным.
 */
export function strictestTextSpec(placements, format) {
    const relevant = (placements || []).filter((p) => p.format === format);
    if (!relevant.length) return { title_max: 60, text_max: 200, cta_max: 24 };

    const pick = (key, fallback) => Math.min(
        ...relevant.map((p) => Number(p.text_spec?.[key] || fallback))
    );
    return {
        title_max: pick('title_max', 60),
        text_max: pick('text_max', 200),
        cta_max: pick('cta_max', 24),
    };
}


/** media_spec первой площадки нужного формата — они одинаковые для формата. */
export function mediaSpecForFormat(placements, format) {
    const found = (placements || []).find((p) => p.format === format);
    return found?.media_spec || {};
}